import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  RadioGroup,
  Radio,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Button,
  Select,
} from "@chakra-ui/react";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../config/apiConfig";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaArrowUp, FaArrowDown, FaEdit, FaTrash } from "react-icons/fa";
import ReactPaginate from "react-paginate";
import './css/paginate.css';

function BotInstruction() {
  // State variables for instructions, pagination and form data
  const [instructions, setInstructions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // For modal form, we use one state object to manage input data.
  // Note: sequence_number is determined automatically on the server.
  const [formData, setFormData] = useState({
    bot_type: "suggest_bot",
    instruction_text: "",
  });
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  // isEdit indicates whether we are editing an existing instruction or adding a new one.
  const [isEdit, setIsEdit] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  // Filter state for bot type (default "suggest_bot")
  const [filterType, setFilterType] = useState("suggest_bot");

  // Chakra UI modal controls
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetch instructions with pagination and optional filter
  const fetchInstructions = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/site/get-bot-instructions`, {
        pageNumber,
        pageSize,
        botType: filterType,
      });
      setInstructions(res.data.instructions);
      setTotalCount(res.data.totalCount);
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการดึงข้อมูลคำสั่ง:", error);
    }
  };

  useEffect(() => {
    fetchInstructions();
  }, [pageNumber, filterType]);

  // Open modal for adding a new instruction
  const openAddModal = () => {
    setIsEdit(false);
    // Set the bot_type to the current filter value
    setFormData({
      bot_type: filterType,
      instruction_text: "",
    });
    onOpen();
  };

  // Open modal for editing an instruction; pre-fill form data.
  const openEditModal = (instruction) => {
    setIsEdit(true);
    setSelectedInstruction(instruction);
    setFormData({
      bot_type: instruction.bot_type,
      instruction_text: instruction.instruction_text,
    });
    onOpen();
  };

  // Handle input changes in the modal form (for bot_type)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle rich text changes using ReactQuill (for instruction_text)
  const handleTextChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      instruction_text: value,
    }));
  };

  // Submit add or edit action
  const handleSubmit = async () => {
    try {
      if (isEdit) {
        await axios.post(`${API_BASE_URL}/api/site/edit-bot-instruction`, {
          editedInstruction: {
            id: selectedInstruction.id,
            bot_type: formData.bot_type,
            sequence_number: selectedInstruction.sequence_number,
            instruction_text: formData.instruction_text,
          },
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/site/add-bot-instruction`, {
          newInstruction: {
            bot_type: formData.bot_type,
            instruction_text: formData.instruction_text,
          },
        });
      }
      onClose();
      fetchInstructions();
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการส่งข้อมูลคำสั่ง:", error);
    }
  };

  // Delete an instruction with confirmation using Sweetalert2
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: "คำสั่งนี้จะถูกลบอย่างถาวร",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "ใช่, ลบเลย!",
      cancelButtonText: "ยกเลิก",
    });
    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE_URL}/api/site/delete-bot-instruction`, { data: { id } });
        Swal.fire("ลบแล้ว!", "คำสั่งถูกลบแล้ว", "success");
        fetchInstructions();
      } catch (error) {
        console.error("เกิดข้อผิดพลาดในการลบคำสั่ง:", error);
        Swal.fire("เกิดข้อผิดพลาด!", "ไม่สามารถลบคำสั่งได้", "error");
      }
    }
  };

  // Reorder items: move up in the list and update order in the backend
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newList = [...instructions];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    newList.forEach((instr, i) => {
      instr.sequence_number = i + 1;
    });
    setInstructions(newList);
    updateOrder(newList);
  };

  // Reorder items: move down in the list and update order in the backend
  const handleMoveDown = (index) => {
    if (index === instructions.length - 1) return;
    const newList = [...instructions];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    newList.forEach((instr, i) => {
      instr.sequence_number = i + 1;
    });
    setInstructions(newList);
    updateOrder(newList);
  };

  // Update order in the backend
  const updateOrder = async (newList) => {
    try {
      const orders = newList.map((instr) => ({
        id: instr.id,
        sequence_number: instr.sequence_number,
      }));
      await axios.post(`${API_BASE_URL}/api/site/change-bot-instruction-order`, { orders });
      fetchInstructions();
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการอัปเดตลำดับ:", error);
    }
  };

  return (
    <Box p={8} bg="gray.50" minH="100vh">
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <RadioGroup onChange={setFilterType} value={filterType} colorScheme="blue">
          <Flex>
            <Radio value="suggest_bot" mr={4}>
              บอทแนะนำ
            </Radio>
            <Radio value="diagnosis_bot">บอทวินิจฉัย</Radio>
          </Flex>
        </RadioGroup>
        <Flex alignItems="center">
          <Text fontSize="l" fontWeight="bold" mr={2}>
            คำสั่งของบอท {filterType === "suggest_bot" ? "แนะนำ" : "วินิจฉัย"} (ทั้งหมด: {totalCount} คำสั่ง)
          </Text>
          <Button colorScheme="green" onClick={openAddModal}>
            เพิ่มคำสั่งใหม่
          </Button>
        </Flex>
      </Flex>
      <Flex direction="column" justifyContent="center" mb={6}>
        {instructions.map((instruction, index) => (
          <Box
            key={instruction.id}
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            shadow="md"
            w="100%"
            mx="auto"
            mb={4}
          >
            <Box p={2}>
              <Text
                fontSize="md"
                dangerouslySetInnerHTML={{ __html: instruction.instruction_text }}
                sx={{
                    // Restore normal list styling
                    ul: {
                      listStyleType: "disc",
                      paddingLeft: "1.5rem",
                      marginBottom: "1rem",
                    },
                    ol: {
                      listStyleType: "decimal",
                      paddingLeft: "1.5rem",
                      marginBottom: "1rem",
                    },
                    // Increase indentation for Quill’s indent classes
                    ".ql-indent-1": {
                      marginLeft: "2rem",
                    },
                    ".ql-indent-2": {
                      marginLeft: "4rem",
                    },
                    ".ql-indent-3": {
                      marginLeft: "6rem",
                    },
                    // etc. if you need deeper levels
                  }}
              />
            </Box>
            <Flex bg="gray.100" p={1} justify="space-between">
              <Flex>
                <Text fontSize="sm" ml={2}>
                  คำสั่ง: {index + 1}
                </Text>
              </Flex>
              <Flex justifyContent="flex-end">
                <IconButton
                  size="xs"
                  mr={2}
                  onClick={() => handleMoveUp(index)}
                  icon={<FaArrowUp />}
                  aria-label="ขึ้น"
                />
                <IconButton
                  size="xs"
                  mr={2}
                  onClick={() => handleMoveDown(index)}
                  icon={<FaArrowDown />}
                  aria-label="ลง"
                />
                <IconButton
                  size="xs"
                  mr={2}
                  onClick={() => openEditModal(instruction)}
                  icon={<FaEdit />}
                  aria-label="แก้ไข"
                />
                <IconButton
                  size="xs"
                  variant={"link"}
                  colorScheme="red"
                  onClick={() => handleDelete(instruction.id)}
                  icon={<FaTrash />}
                  aria-label="ลบ"
                />
              </Flex>
            </Flex>
          </Box>
        ))}
      </Flex>
      <Box mt={6} mx="auto" width="80%">
        <ReactPaginate
          previousLabel={"ก่อนหน้า"}
          nextLabel={"ถัดไป"}
          breakLabel={"..."}
          pageCount={Math.ceil(totalCount / pageSize)}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={(data) => {
            setPageNumber(data.selected + 1);
          }}
          containerClassName={"pagination"}
          activeClassName={"active"}
        />
      </Box>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEdit ? "แก้ไขคำสั่ง" : "เพิ่มคำสั่งใหม่"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Select name="bot_type" value={formData.bot_type} onChange={handleInputChange} mb={4}>
              <option value="suggest_bot">บอทแนะนำ</option>
              <option value="diagnosis_bot">บอทวินิจฉัย</option>
            </Select>
            <ReactQuill
              theme="snow"
              value={formData.instruction_text}
              onChange={handleTextChange}
              style={{ height: "450px", width: "100%", marginBottom: "16px" }}
            />
          </ModalBody>
          <ModalFooter mt={4}>
            <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
              {isEdit ? "บันทึกการเปลี่ยนแปลง" : "เพิ่มคำสั่งใหม่"}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              ยกเลิก
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default BotInstruction;
